// Module-level locale store with a build-configured default + persistence.
//
// Resolution order (first match wins):
//   1. explicit user choice persisted in localStorage ('cogo-locale')
//   2. the deployment's configured default (VITE_COGO_DEFAULT_LOCALE)
//   3. DEFAULT_LOCALE
//
// Shared across the marketing tree (nav switcher + page bodies) via a tiny
// subscribe/notify store, consumed with useSyncExternalStore in ./use-locale.

import { DEFAULT_LOCALE, isLocaleCode, type LocaleCode } from './locales'

const STORAGE_KEY = 'cogo-locale'

/**
 * The locale this build opens in before anyone has chosen one.
 *
 * The public multi-tenant build leaves `VITE_COGO_DEFAULT_LOCALE` unset and so
 * keeps opening in `DEFAULT_LOCALE`. A single-tenant deployment — an appliance
 * installed at one site, whose operators all read one language — sets it at
 * build time so its people are not asked to re-pick the same language on every
 * fresh browser profile. It is a *default*, not a lock: the switcher still
 * writes an explicit choice to localStorage, and rule 1 above outranks this.
 *
 * An unrecognised value falls back rather than throwing: a bad build argument
 * must not be able to blank the app at module scope.
 */
export function buildDefaultLocale(): LocaleCode {
  const configured = (
    import.meta.env as Record<string, string | undefined> | undefined
  )?.VITE_COGO_DEFAULT_LOCALE?.trim()
  return isLocaleCode(configured) ? configured : DEFAULT_LOCALE
}

function detectInitialLocale(): LocaleCode {
  if (typeof window === 'undefined') return buildDefaultLocale()

  // 1. explicit, persisted choice
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (isLocaleCode(saved)) return saved
  } catch {
    /* localStorage unavailable (private mode / blocked) — fall through */
  }

  // Do not infer the Studio language from the browser: a browser's language
  // list says what the person reads, not which deployment they are standing
  // in, and inferring made the language flicker between machines. The build
  // states it instead, and the person can always override it.
  return buildDefaultLocale()
}

let current: LocaleCode = detectInitialLocale()

// Reflect the initially-resolved locale on <html lang> at module init. setLocale()
// keeps it in sync on later changes; without this, a persisted / auto-detected
// non-default locale would leave <html lang> stale (SEO / a11y).
if (typeof document !== 'undefined') {
  try {
    document.documentElement.lang = current
  } catch {
    /* ignore (non-DOM env) */
  }
}

const listeners = new Set<() => void>()

export function getLocale(): LocaleCode {
  return current
}

export function setLocale(next: LocaleCode): void {
  if (next === current) return
  current = next
  try {
    window.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* ignore persistence failure */
  }
  try {
    document.documentElement.lang = next
  } catch {
    /* ignore (non-DOM env) */
  }
  listeners.forEach((fn) => fn())
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
